import classNames from 'classnames';
import React, { useContext, useMemo, useRef, useState, useCallback } from 'react';
import { noop, isArray, first, isEmpty } from 'underscore';
import PropTypes from 'prop-types';
import { mergeStyles } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Collapse,
  useCollapseState,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { ValidatedLegacyCallToActionWithLanguageSection } from '@bingads-webui-campaign-react/legacy-call-to-action';
import { CopilotRepositoryProvider } from '@bingads-webui/inline-copilot';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import {
  ValidatedFinalUrlWithRecommendationsSection,
  LongHeadlineAssetCollectionSection,
  HeadlineAssetCollectionSection,
  DescriptionAssetCollectionSection,
  ValidatedBusinessNameWithRecommendationSection,
} from '@bingads-webui-campaign-react/pmax-text-input';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { withScope } from '@bingads-webui-react/scope-service';
import {
  ImageListPortalContainerWithCarouselSection,
  LogoListPortalContainerSection,
  ValidatedImageExperienceWrapper,
} from '@bingads-webui-campaign-react/pmax-image-experience';
import { constants } from '@bingads-webui-campaign-react/image-experience';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useCopilot } from '@bingads-webui-campaign-react/copilot-experience';

import { getMMAEditorStyles } from './copilot-search-mma-editor-styles';
import { AdvancedSettings } from './advanced-settings';
import { getUnifiedAdPreviewConfig } from './get-unified-ad-preview-config';
import { SearchMMAAdPreview } from './search-mma-ad-preview';
import { errorMapper } from './utils/errors-util';
import { useAutomatedCTA } from './useAutomatedCTA';
import { useInitAdCustomizer } from './useInitAdCustomizer';
import { useEvaluatedAssests } from './useEvaluatedAssets';

const advancedSettingsStyles = mergeStyles({
  marginBottom: 40,
});

const featureName = 'searchMMA';
const entity = 'ResponsiveAd';

export const CopilotSearchMMAEditorContainerInternal = ({
  data,
  errors,
  onDataChange,
  onReset,
  scope,
  perfMarker,
  permissions,
  viewContext,
  genericImage,
  showAllValidationErrors,
  showUnmappedFormErrors,
  triggerTestUurl,
  adId,
  newI18n,
}) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const instrumentation = useContext(InstrumentationContext);
  const errorMapperWithI18n = useMemo(() => error => errorMapper(error, i18n), [i18n]);
  const defaultPreviewFields = useMemo(() => ({
    shortHeadline: i18n.getString(_TL_('Short headline')),
    longHeadline: i18n.getString(_TL_('Long headline')),
    description: i18n.getString(_TL_('Ad text')),
    businessName: i18n.getString(_TL_('Business name')),
    callToAction: i18n.getString(_TL_('Shop Now')),
    url: 'https://example.com',
  }), [i18n]);

  const { cid, aid } = scope.values || {};
  const campaignId = viewContext.currentCampaign && viewContext.currentCampaign.Id;

  const classes = useDefaultStyles(getMMAEditorStyles);

  const finalUrls = data.FinalUrls;
  const firstUrl = finalUrls && first(finalUrls);
  const finalUrlForRecommendation = data.FinalUrlForRecommendation;
  const businessName = data.BusinessName;
  const mobileFinalUrls = data.MobileFinalUrls;
  const headlines = data.Headlines;
  const longHeadlines = data.LongHeadlines;
  const descriptions = data.Descriptions;
  const trackingUrlTemplate = data.TrackingUrlTemplate;
  const finalUrlSuffix = data.FinalUrlSuffix;
  const urlCustomParameters = data.UrlCustomParameters;
  const images = data.Images;
  const callToAction = data.CallToAction;
  const imageList = images && images.ImageList;
  const [ctaText, setCTAText] = useState(callToAction && callToAction.cta);
  const {
    evaluatedHeadLine,
    evaluatedLongHeadLine,
    evaluatedDescription,
  } = useEvaluatedAssests({
    headline: headlines && headlines.Headline0,
    longHeadline: longHeadlines && longHeadlines.LongHeadline0,
    description: descriptions && descriptions.Description0,
  });

  const imageData = useMemo(() => ({
    Images: images,
    Headlines: headlines && headlines.Headline0 ? { ...headlines, Headline0: evaluatedHeadLine } : headlines,
    LongHeadlines: longHeadlines && longHeadlines.LongHeadline0 ? { ...longHeadlines, LongHeadline0: evaluatedLongHeadLine } : longHeadlines,
    Descriptions: descriptions && descriptions.Description0 ? { ...descriptions, Description0: evaluatedDescription } : descriptions,
    BusinessName: businessName,
    CallToAction: ctaText,
    FinalUrls: finalUrls,
  }), [businessName, ctaText, descriptions, evaluatedDescription, evaluatedHeadLine, evaluatedLongHeadLine, finalUrls, headlines, images, longHeadlines]);

  const previewData = useMemo(() => ({
    shortHeadline: isEmpty(evaluatedHeadLine) ? defaultPreviewFields.shortHeadline : evaluatedHeadLine,
    longHeadline: isEmpty(evaluatedLongHeadLine) ? defaultPreviewFields.longHeadline : evaluatedLongHeadLine,
    description: isEmpty(evaluatedDescription) ? defaultPreviewFields.description : evaluatedDescription,
    businessName: isEmpty(businessName) ? defaultPreviewFields.businessName : businessName,
    url: isEmpty(firstUrl) ? defaultPreviewFields.url : firstUrl,
    callToAction: isEmpty(ctaText) ? defaultPreviewFields.callToAction : ctaText,
  }), [businessName, ctaText, evaluatedDescription, evaluatedHeadLine, evaluatedLongHeadLine, firstUrl]);

  const [
    allAdSettingsSections,
    adSettingsExpandedSections,
    setAdSettingsExpandedSections,
  ] = useCollapseState(
    ['final-url', 'images', 'logos', 'headlines', 'long-headlines', 'descriptions', 'business-name', 'call-to-action', 'mobile-final-url', 'advanced-url-options'] /* all sections */,
    ['final-url', 'images', 'logos', 'headlines', 'long-headlines', 'descriptions', 'business-name'] /* initially expanded sections */
  );

  const maxUploadNum = useMemo(() => {
    const imageListLength = imageList && isArray(imageList) ? imageList.length : 0;
    return constants.DEFAULT_MAX_IMAGE - imageListLength;
  }, [imageList]);

  // copilot states and actions
  const [addNewDescriptionCallback, setAddNewDescriptionCallback] = useState(noop);
  const [addNewShortHeadlinesCallback, setAddNewShortHeadlinesCallback] = useState(noop);
  const [addNewLongHeadlinesCallback, setAddNewLongHeadlinesCallback] = useState(noop);
  const [finalUrlForCopilotRecommendations, setFinalUrlForCopilotRecommendations] = useState(first(finalUrls));

  const imagesContainerRef = useRef(null);
  const logosContainerRef = useRef(null);
  const [imageExperiencePortalContainerState, setImageExperiencePortalContainerState] = useState({});
  const [previewImagesSharedState, setPreviewImagesSharedState] = useState({ previewImageId: null, cropImageObj: null });
  const [imageExperienceAddNewImageCallback, setImageExperienceAddNewImageCallback] = useState(noop);
  const [newCTACallback, setNewCTACallback] = useState(noop);

  const allAssetsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdSettingsExpandedSections }),
    [setAdSettingsExpandedSections]
  );

  const {
    copilotBanner,
    suggestionPoller,
  } = useCopilot({
    maxUploadNum,
    addNewShortHeadlinesCallback,
    addNewLongHeadlinesCallback,
    addNewDescriptionCallback,
    imageExperienceAddNewImageCallback,
    newCTACallback,
    currentCTA: callToAction && callToAction.cta,
    onReset,
    cid,
    aid,
    serverContext,
    instrumentation,
    data: isEmpty(data) ? undefined : data,
    campaignId,
    assetGroupId: adId,
    feature: 'MMA',
  });

  const updateFinalUrlForCopilot = useCallback(() => {
    setFinalUrlForCopilotRecommendations(finalUrlForRecommendation);
  }, [finalUrlForRecommendation]);

  const updateFinalUrlCallback = useCallback((newFinalUrls) => {
    onDataChange({
      data: {
        ...data,
        FinalUrls: newFinalUrls,
      },
    });
  }, [data, onDataChange]);

  const onCallActionLanguageChange = useCallback((updates) => {
    setCTAText(updates.ctaText);
  }, []);

  const adPreviewConfig = getUnifiedAdPreviewConfig({ isMultimediaAd: true });
  const automatedCTAEnable = useAutomatedCTA({
    isMMAAutomatedCTAOptInEnabled: true,
    campaignId,
  });

  useInitAdCustomizer({
    viewContext,
    permissions,
    i18n: newI18n,
  });

  return (
    <div className={classNames(classes.adEditorShell, 'mma-ad-editor', 'crud-audience-ad-react', 'crud-multimedia-ad-react')}>
      <div className={classNames('ad-edit-and-preview-container')}>
        <ActuationGroup actuationProps={{ isActive: showAllValidationErrors }}>
          <AttentionBeaconGroup>
            <FormErrorGroup formErrorProps={{ errors, showAllValidationErrors, errorMapper: errorMapperWithI18n }}>
              <ValidationGroup
                validationProps={{
                  schema: AssetGroup,
                  onValidate: onDataChange,
                  data,
                  i18n,
                }}
              >
                <CopilotRepositoryProvider
                  campaignId={campaignId}
                  finalUrl={finalUrlForCopilotRecommendations}
                  locale={i18n.getLocale()}
                  recommendationType="MMA"
                  perfMarker={perfMarker}
                  serverContext={serverContext}
                  enableLogoAndCTA={permissions.dynamic.CopilotCTALogoGeneration}
                >
                  {showUnmappedFormErrors && (
                    <div className={classes.headerStackStyles}>
                      <div className="server-errors">
                        <UnmappedFormErrors mapper={errorMapperWithI18n} />
                      </div>
                    </div>
                  )}
                  <div className={classes.container}>
                    <div className={classNames(classes.leftStyles, 'left-column')}>
                      <div className="final-url-container">
                        <ValidatedFinalUrlWithRecommendationsSection
                          permissions={permissions}
                          finalUrls={finalUrls}
                          onFinalURLFocusOut={updateFinalUrlForCopilot}
                          onFinalURLEnterKeyUp={updateFinalUrlForCopilot}
                          entity={entity}
                          key="final-url"
                        />
                      </div>
                      { copilotBanner }
                      <Collapse allPanelItems={allAdSettingsSections} checkItems skipItemsTypeCheck activeKeys={adSettingsExpandedSections} setActiveKeys={setAdSettingsExpandedSections}>
                        <ImageListPortalContainerWithCarouselSection
                          containerRef={imagesContainerRef}
                          containerState={imageExperiencePortalContainerState}
                          addImageCallback={imageExperienceAddNewImageCallback}
                          imageLimit={constants.DEFAULT_MAX_IMAGE}
                          maxUploadNum={maxUploadNum}
                          expand={allAssetsExpand}
                          images={images}
                          classes={classes}
                          key="images"
                        />
                        <LogoListPortalContainerSection
                          containerRef={logosContainerRef}
                          containerState={imageExperiencePortalContainerState}
                          expand={allAssetsExpand}
                          images={images}
                          key="logos"
                        />
                        <HeadlineAssetCollectionSection
                          featureName={featureName}
                          headlines={headlines}
                          expand={allAssetsExpand}
                          setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback}
                          key="headlines"
                        />
                        <LongHeadlineAssetCollectionSection
                          featureName={featureName}
                          longHeadlines={longHeadlines}
                          isOptional
                          expand={allAssetsExpand}
                          setAddNewLongHeadlinesCallback={setAddNewLongHeadlinesCallback}
                          key="long-headlines"
                        />
                        <DescriptionAssetCollectionSection
                          featureName={featureName}
                          descriptions={descriptions}
                          expand={allAssetsExpand}
                          setAddNewDescriptionCallback={setAddNewDescriptionCallback}
                          key="descriptions"
                        />
                        <ValidatedBusinessNameWithRecommendationSection
                          businessName={businessName}
                          expand={allAssetsExpand}
                          entity={entity}
                          key="business-name"
                        />
                        <ValidatedLegacyCallToActionWithLanguageSection
                          callToAction={callToAction}
                          isMultimediaAd
                          isDisplayPlusMMA={false}
                          key="call-to-action"
                          onChange={onCallActionLanguageChange}
                          setNewCTACallback={setNewCTACallback}
                          isMMAAutomatedCTAOptInEnabled
                          automatedCTAEnable={automatedCTAEnable}
                        />
                      </Collapse>
                      <div className={advancedSettingsStyles}>
                        <AdvancedSettings
                          mobileFinalUrls={mobileFinalUrls}
                          trackingUrlTemplate={trackingUrlTemplate}
                          finalUrlSuffix={finalUrlSuffix}
                          urlCustomParameters={urlCustomParameters}
                          triggerTestUurl={triggerTestUurl}
                        />
                      </div>
                    </div>
                    <div className={classNames(classes.rightStyles, 'right-column')}>
                      <div className={classes.mmaAdImagePreviewCarousel}>
                        <SearchMMAAdPreview
                          data={data}
                          onDataChange={onDataChange}
                          permissions={permissions}
                          genericImage={genericImage}
                          previewTexts={previewData}
                          previewImagesSharedState={previewImagesSharedState}
                          setPreviewImagesSharedState={setPreviewImagesSharedState}
                        />
                      </div>
                      <div>
                        <ValidatedImageExperienceWrapper
                          i18n={i18n}
                          permissions={permissions}
                          campaignId={campaignId}
                          imagesContaineRef={imagesContainerRef}
                          logosContaineRef={logosContainerRef}
                          isLogoEnabled
                          isBoostPortraitEnabled={false}
                          updateFinalUrlCallback={updateFinalUrlCallback}
                          previewImagesSharedState={previewImagesSharedState}
                          setPreviewImagesSharedState={setPreviewImagesSharedState}
                          updatePortalContainerState={setImageExperiencePortalContainerState}
                          setImageExperienceAddNewImageCallback={setImageExperienceAddNewImageCallback}
                          validationProps={{ data: imageData, initialValue: imageData }}
                          suggestionPoller={suggestionPoller}
                          isAd
                          wizardPerfMarker={perfMarker}
                          isCombinedMsanAndMmaAspectRatios={false}
                          customMaxImageCount={constants.DEFAULT_MAX_IMAGE}
                          UAPLPMaxIntegration={permissions.dynamic.AdPreviewLibrarySearchMMA}
                          isMSANBoostEnabled={false}
                          isImproveMMA={false}
                          isMultimediaAd
                          isMultimediaAdinMSANEnabled={false}
                          imageErrorMessages={[]}
                          genericImage={genericImage}
                          adPreviewConfig={adPreviewConfig}
                          showCopilotOptionForImage={permissions.IsBackgroundSceneEnabled}
                        />
                      </div>
                    </div>
                  </div>
                </CopilotRepositoryProvider>
              </ValidationGroup>
            </FormErrorGroup>
          </AttentionBeaconGroup>
        </ActuationGroup>
      </div>
    </div>
  );
};

CopilotSearchMMAEditorContainerInternal.propTypes = {
  data: PropTypes.shape({
    FinalUrls: PropTypes.string,
    MobileFinalUrls: PropTypes.string,
    BusinessName: PropTypes.string,
    Headlines: PropTypes.arrayOf(PropTypes.string),
    LongHeadlines: PropTypes.arrayOf(PropTypes.string),
    Descriptions: PropTypes.arrayOf(PropTypes.string),
    Images: PropTypes.shape({
      ImageList: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    TrackingUrlTemplate: PropTypes.string,
    FinalUrlSuffix: PropTypes.string,
    UrlCustomParameters: PropTypes.string,
    FinalUrlForRecommendation: PropTypes.string,
    CallToAction: PropTypes.shape({
      cta: PropTypes.string,
      language: PropTypes.string,
    }),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.string,
      aid: PropTypes.string,
    }),
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AdPreviewLibrarySearchMMA: PropTypes.bool,
      CopilotCTALogoGeneration: PropTypes.bool,
    }),
    IsBackgroundSceneEnabled: PropTypes.bool,
  }).isRequired,
  genericImage: PropTypes.string.isRequired,
  viewContext: PropTypes.shape({
    currentCampaign: PropTypes.shape({
      Id: PropTypes.string,
    }),
  }).isRequired,
  showAllValidationErrors: PropTypes.bool.isRequired,
  showUnmappedFormErrors: PropTypes.bool,
  triggerTestUurl: PropTypes.func.isRequired,
  adId: PropTypes.number,
  newI18n: PropTypes.shape({}).isRequired,
};

CopilotSearchMMAEditorContainerInternal.defaultProps = {
  showUnmappedFormErrors: true,
  adId: null,
};

export const CopilotSearchMMAEditorContainer = withScope(CopilotSearchMMAEditorContainerInternal);
